import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../App';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';
import { Logo } from '../Logo';
import {
  LayoutDashboard, CalendarDays, Ticket, UserCircle, LogOut, Menu, Zap, X, Wallet, BadgeIndianRupee
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { promoterApi } from '../../lib/api';

const BASE_NAV = [
  { path: '/account', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/account/wallet', label: 'Scratch Points', icon: Wallet },
  { path: '/account/events', label: 'Upcoming Events', icon: CalendarDays },
  { path: '/account/bookings', label: 'My Bookings', icon: Ticket },
  { path: '/account/profile', label: 'My Profile', icon: UserCircle },
];

// Promoter link — only shown if the logged-in customer IS a promoter.
const PROMOTER_NAV = { path: '/account/promoter', label: 'Promoter', icon: BadgeIndianRupee };

function useIsPromoter() {
  const [isPromoter, setIsPromoter] = useState(false);
  useEffect(() => {
    let alive = true;
    promoterApi.me()
      .then(() => alive && setIsPromoter(true))
      .catch(() => alive && setIsPromoter(false));
    return () => { alive = false; };
  }, []);
  return isPromoter;
}

function NavItem({ item, onClick }) {
  return (
    <NavLink
      to={item.path}
      end={item.exact}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-5 py-3.5 text-[15px] font-medium transition-all duration-200',
          'active:scale-[0.98]', // Touch feedback
          isActive
            ? 'sidebar-active pl-[calc(1.25rem-2px)]'
            : 'text-[#B9B9B9] hover:text-[#F2F2F2] hover:bg-[#1A1A1A]'
        )
      }
    >
      <item.icon size={18} strokeWidth={2} />
      {item.label}
    </NavLink>
  );
}

function SidebarContent({ customer, onLogout, onNavClick }) {
  const initials = customer?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  const isPromoter = useIsPromoter();
  const items = isPromoter ? [...BASE_NAV, PROMOTER_NAV] : BASE_NAV;
  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-2">
          <Logo variant="icon" height={32} />
          <div>
            <p className="text-[10px] text-[#B9B9B9] uppercase tracking-wider">My Account</p>
          </div>
        </div>
      </div>

      {/* Customer avatar */}
      <div className="px-5 py-4 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center flex-shrink-0">
            <span className="text-[#120F08] text-sm font-bold">{initials}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm text-[#F2F2F2] font-semibold truncate">{customer?.full_name}</p>
            <p className="text-xs text-[#B9B9B9] truncate">{customer?.phone}</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 space-y-0.5">
        {items.map(item => <NavItem key={item.path} item={item} onClick={onNavClick} />)}
      </nav>

      {/* Logout */}
      <div className="border-t border-[#2A2A2A] p-4">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-[#B9B9B9] hover:text-[#F2F2F2] hover:bg-[#1A1A1A] gap-2"
          onClick={onLogout}
          data-testid="customer-logout-button"
        >
          <LogOut size={14} /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function CustomerShell({ children, title, actions, hideHeader }) {
  const { customer, logoutCustomer } = useCustomerAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logoutCustomer(); navigate('/account/login'); };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0A0A0A' }}>
      {/* Desktop sidebar - visible only on large screens */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#2A2A2A] fixed inset-y-0 left-0 z-30" style={{ backgroundColor: '#111111' }}>
        <SidebarContent customer={customer} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer - smooth slide animation */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent 
          side="left" 
          className="p-0 w-[280px] border-r border-[#2A2A2A]" 
          style={{ backgroundColor: '#111111' }}
        >
          <SidebarContent customer={customer} onLogout={handleLogout} onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content - mobile-first layout */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile-optimized topbar */}
        {!hideHeader && (
          <header 
            className="sticky top-0 z-20 border-b border-[#2A2A2A] flex items-center gap-3 backdrop-blur-sm" 
            style={{ 
              backgroundColor: 'rgba(17, 17, 17, 0.95)',
              padding: '0.75rem 1rem'
            }}
          >
            {/* Hamburger button - prominent on mobile */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] text-[#D4A843] transition-all active:scale-95"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
            >
              <Menu size={22} strokeWidth={2.5} />
            </button>
            
            {/* Title - mobile optimized */}
            <div className="flex-1 min-w-0">
              {title && (
                <h1 className="font-['EB_Garamond'] text-[17px] lg:text-lg text-[#F2F2F2] font-semibold truncate">
                  {title}
                </h1>
              )}
            </div>
            
            {/* Actions */}
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </header>
        )}
        
        {/* Content area - maximum space on mobile */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 max-w-7xl w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
